import React, { Fragment, useState } from "react";

import * as Yup from "yup";
import AddIcon from "@mui/icons-material/Add";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RemoveIcon from "@mui/icons-material/Remove";
import { Box, Button, IconButton, Menu, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import { componentNameToTitle, COVENANT_TYPES } from "constants/covanents";
import { FieldArray, FormikProvider, useFormik } from "formik";
import { useActionHook } from "hooks";
import { useDealActions } from "store/actions-context";
import { Covenant, CovenantComponentPart } from "store/types/deal/covanents";
import { Measure } from "store/types/deal/measure";

interface CovenantSettingsViewProps {
  covenant: Covenant;
  measures: Measure[];
  onBack: () => void;
  isEdit: boolean;
  className?: string;
  onSuccess?: () => void;
}

export const CovenantSettingsView: React.FC<CovenantSettingsViewProps> = ({
  covenant,
  measures,
  onBack,
  isEdit,
  onSuccess
}) => {
  const [directionMenuIdx, setDirectionMenuIdx] = useState<{ componentName: string; partIdx: number } | null>(null);
  const [directionMenuAnchor, setDirectionMenuAnchor] = useState<null | HTMLElement>(null);

  const { updateCovenantAction, createCovenantAction } = useDealActions();

  const { action, loading } = useActionHook(covenant.id ? updateCovenantAction : createCovenantAction, {
    onSuccess: onSuccess
  });

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    type: Yup.string().required("Type is required"),
    components: Yup.object().test(
      "components",
      "Each part must have a measure selected",
      (components: Record<string, CovenantComponentPart[]> | undefined) => {
        if (!components) return false;
        return Object.values(components).every(
          (parts) => Array.isArray(parts) && parts.every((part) => part.measure_id)
        );
      }
    ),
    min_value: Yup.number().required("Minimum value is required"),
    max_value: Yup.number()
      .required("Maximum value is required")
      .min(Yup.ref("min_value"), "Maximum value must be greater than minimum value")
  });

  const formik = useFormik({
    initialValues: {
      id: covenant?.id || undefined,
      deal_id: covenant?.deal_id || 0,
      name: covenant?.name || "",
      type: covenant?.type || "",
      components: covenant?.components || {},
      min_value: covenant?.min_value ?? "",
      max_value: covenant?.max_value ?? ""
    },
    validationSchema,
    onSubmit: () => {
      action(formik.values);
    }
  });

  const handleDirectionClick = (componentName: string, idx: number, event: React.MouseEvent<HTMLElement>) => {
    setDirectionMenuIdx({ componentName, partIdx: idx });
    setDirectionMenuAnchor(event.currentTarget);
  };

  const handleDirectionSelect = (dir: 1 | -1) => {
    if (directionMenuIdx) {
      formik.setFieldValue(`components.${directionMenuIdx.componentName}.${directionMenuIdx.partIdx}.direction`, dir);
    }
  };

  const handleDirectionClose = () => {
    setDirectionMenuIdx(null);
    setDirectionMenuAnchor(null);
  };

  return (
    <AsyncContentMui isLoading={loading}>
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          {!isEdit && (
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <IconButton onClick={onBack}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="subtitle1">
                Define {COVENANT_TYPES.find((t) => t.key === formik.values.type)?.label}
              </Typography>
            </Stack>
          )}
          <Typography variant="h5" fontWeight={600} mb={1}>
            {formik.values.name || "Covenant"}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" mb={2}>
            Formula: Debt / EBITDA <InfoOutlinedIcon fontSize="sm" sx={{ ml: 1, verticalAlign: "middle" }} />
          </Typography>
          <Box mb={3}>
            {Object.entries(formik.values.components).map(([componentName, parts]) => (
              <Box key={componentName} mb={3}>
                <Stack direction="row" alignItems="center" mb={1} gap={1}>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {componentNameToTitle[componentName as keyof typeof componentNameToTitle]}
                  </Typography>
                  <InfoOutlinedIcon fontSize="sm" />
                </Stack>
                <FieldArray name={`components.${componentName}`}>
                  {({ remove, push }) => (
                    <>
                      <Stack direction="row" gap={2} flexWrap="wrap" alignItems="baseline" mb={2}>
                        {parts.map((part, idx) => (
                          <Fragment key={idx}>
                            {idx > 0 && (
                              <IconButton size="sm" onClick={(e) => handleDirectionClick(componentName, idx, e)}>
                                {part.direction > 0 ? <AddIcon color="primary" /> : <RemoveIcon color="error" />}
                              </IconButton>
                            )}
                            <Box
                              sx={{
                                position: "relative",
                                minWidth: 200,
                                width: 200,
                                flex: 1,
                                display: "flex",
                                alignItems: "center",
                                ":hover .remove-param": { opacity: 1 }
                              }}
                            >
                              <TextField
                                select
                                label={`Parameter #${idx + 1}`}
                                name={`components.${componentName}.${idx}.measure_id`}
                                value={part.measure_id}
                                sx={{ width: "100%" }}
                                size="small"
                                onChange={formik.handleChange}
                                error={
                                  formik.touched.components?.[componentName]?.[idx]?.measure_id && !part.measure_id
                                }
                                helperText={
                                  formik.touched.components?.[componentName]?.[idx]?.measure_id && !part.measure_id
                                    ? "Measure is required"
                                    : ""
                                }
                              >
                                {measures.map((m) => (
                                  <MenuItem key={m.id} value={m.id}>
                                    {m.name}
                                  </MenuItem>
                                ))}
                              </TextField>
                              <IconButton
                                className="remove-param"
                                size="sm"
                                sx={{
                                  position: "absolute",
                                  right: 24,
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  opacity: 0,
                                  transition: "opacity 0.2s"
                                }}
                                onClick={() => remove(idx)}
                              >
                                <CloseIcon fontSize="sm" />
                              </IconButton>
                            </Box>
                          </Fragment>
                        ))}
                      </Stack>
                      <Button
                        variant="outlined"
                        size="sm"
                        startIcon={<AddCircleOutlineIcon />}
                        sx={{ borderRadius: 20, height: 36, ml: 1, px: 2, fontWeight: 400, fontSize: 12 }}
                        onClick={() => push({ measure_id: "", direction: 1 })}
                      >
                        Add More
                      </Button>
                    </>
                  )}
                </FieldArray>
              </Box>
            ))}
          </Box>
          <Box mb={3}>
            <Stack direction="row" alignItems="center" gap={1} mb={1}>
              <Typography variant="h6">Level</Typography>
              <InfoOutlinedIcon fontSize="sm" />
            </Stack>
            <Stack direction="row" gap={2}>
              <TextField
                label="Minimum"
                name="min_value"
                value={formik.values.min_value}
                onChange={formik.handleChange}
                size="small"
                error={formik.touched.min_value && !!formik.errors.min_value}
                helperText={formik.touched.min_value && formik.errors.min_value}
              />
              <TextField
                label="Maximum"
                name="max_value"
                value={formik.values.max_value}
                onChange={formik.handleChange}
                size="small"
                error={formik.touched.max_value && !!formik.errors.max_value}
                helperText={formik.touched.max_value && formik.errors.max_value}
              />
            </Stack>
          </Box>
          <Stack direction="row" justifyContent="flex-end" gap={2} mt={4}>
            <Button variant="outlined">Cancel</Button>
            <Button variant="contained" color="primary" type="submit">
              Apply
            </Button>
          </Stack>
          <DirectionMenu
            anchorEl={directionMenuAnchor}
            open={directionMenuIdx !== null}
            onClose={handleDirectionClose}
            onSelect={handleDirectionSelect}
          />
        </form>
      </FormikProvider>
    </AsyncContentMui>
  );
};

const DirectionMenu: React.FC<{
  anchorEl: null | HTMLElement;
  open: boolean;
  onClose: () => void;
  onSelect: (dir: 1 | -1) => void;
}> = ({ anchorEl, open, onClose, onSelect }) => (
  <Menu anchorEl={anchorEl} open={open} onClose={onClose}>
    <MenuItem
      onClick={() => {
        onSelect(1);
        onClose();
      }}
    >
      <AddIcon fontSize="sm" sx={{ mr: 1 }} /> Plus
    </MenuItem>
    <MenuItem
      onClick={() => {
        onSelect(-1);
        onClose();
      }}
    >
      <RemoveIcon fontSize="sm" sx={{ mr: 1 }} /> Minus
    </MenuItem>
  </Menu>
);
